import { FormControl, FormGroup } from '@angular/forms';
import { Code15 } from './chip-code.type';
import { Veterinarian } from '@app/pages/clinic/models/clinic.model';

export type ChipModel = {
    id: string;
    code: Code15;
    lost: boolean;
    stolen: boolean;
    dead: boolean;
}

export type ChipGridInfo = {
    registeredChip: {
        isEmailSent: boolean | null;
        date: string;
        pdfUrl: string | null;
    } | null;
} & ChipModel

export type ChipCodeForm = {
    code: FormControl<string | null>;
}
export type SignatureType = 'ownerSignature' | 'vetSignature' | 'previousOwnerSignature';

export type Signaturs = {
    veterinarian: string | null,
    owner: string | null,
    previousOwner: string | null,
}

export type step = 'veterinarian' | 'owner' | 'previousOwner';

export type AnimalType = 'dog' | 'cat' | 'rabbitFerry';
export const animalTypes = ['dog', 'cat', 'rabbitFerry'] as const satisfies AnimalType[];

export type ChipFormType = 'Registration' | 'ChangeOwner' | 'ChangeAddress';

export type ChipInformationForm = {
    vetId: FormControl<string>;
    selectedVetDocumentId: FormControl<string>;
    date: FormControl<Date | string>;
    chipReaded: FormControl<boolean>;
}

export type OwnerForm = {
    idType: FormControl<number>;
    idNumber: FormControl<string>;
    nameSurname: FormControl<string>;
    countryId: FormControl<number>;
    regionId: FormControl<number | null>;
    settlementId: FormControl<number | null>;
    address: FormControl<string>;
    zipCode: FormControl<string>;
    phone: FormControl<string>;
    email: FormControl<string>;
    isAddressSameAsOwner?: FormControl<boolean>;
}

export type AnimalForm = {
    type: FormControl<AnimalType>;
    breed: FormControl<number | null>;
    name: FormControl<string>;
    sex: FormControl<number>;
    dateOfBirth: FormControl<Date | string>;
    chipLocation: FormControl<number>;
    coatType: FormControl<number>;
    size: FormControl<number>;
    castrated: FormControl<boolean>;
    isAddressSameAsOwner: FormControl<boolean>;
}

export type AnimalAddressForm = {
    countryId: FormControl<number | null>;
    regionId: FormControl<number | null>;
    settlementId: FormControl<number | null>;
    address: FormControl<string | null>;
    zipCode: FormControl<string | null>;
}

export type ChipRegistrationRootForm = {
    chip: FormGroup<ChipCodeForm>;
    chipInformation: FormGroup<ChipInformationForm>;
    owner: FormGroup<OwnerForm>;
    animal: FormGroup<AnimalForm>;
    animalAddress: FormGroup<AnimalAddressForm>;
    vetSignature?: FormControl<string | null>;
    ownerSignature?: FormControl<string | null>;
    previousOwnerSignature?: FormControl<string | null>;
}

export type Address = {
    countryId: number | string | null;
    regionId: number | null;
    settlementId: number | null;
    address: string | null;
    zipCode: string | null;
    id?: string;
}

export type NewChipPayload = {
    chip: {
        id?: string;
        code: string;
    };
    chipInformation: {
        id?: string;
        date: Date | string;
        chipReaded: boolean;
        veterinaryId: string;
    };
    owner: {
        id?: string;
        idType: string;
        idNumber: string;
        fullName: string;
        email: string;
        phone: string;
    };
    animal: AnimalNewChipPayload;
    address: Address;
    animalAddress?: Address;
    vetSignature?: string;
    ownerSignature?: string;
    previousOwnerSignature?: string | null;
}

export type ChipRegistrationResponse = {
    chip: {
        code: string;
        id: string;
    };
    registeredChip: RegisteredChipUploadResponse;
    owner: {
        idType: string;
        idNumber: string;
        fullName: string;
        email: string;
        phone: string;
        adressId: string;
        id: string;
    };
    animal: AnimalUploadResponse;
    veterinarian: Veterinarian;
    address: Address;
    animalAddress?: Address;
}

export type ChipActionResponse = {
    chip: {
        code: string;
        id: string;
    };
    chipAction: RegisteredChipUploadResponse;
    owner: {
        idType: string;
        idNumber: string;
        fullName: string;
        email: string;
        phone: string;
        adressId: string;
        id: string;
    };
    animal: AnimalUploadResponse;
    veterinarian: Veterinarian;
    address: Address;
    animalAddress?: Address;
}

export type UploadPdfResponse = {
    success: boolean;
    step: string;
    pdfUploaded: boolean;
    emailSent: boolean;
    pdfUrl: string | null;
}

export type AnimalBaseModel = {
    id?: string;
    type: AnimalType;
    breed: number | null;
    name: string;
    sex: number;
    dateOfBirth: Date | string;
    chipLocation: number;
    coatType: number;
    size: number;
    castrated: boolean;
}

export type AnimalNewChipPayload = {
    isAddressSameAsOwner: boolean;
} & AnimalBaseModel

export type AnimalUploadResponse = {
    adressId: string;
} & AnimalBaseModel

export type RegisteredChipUploadResponse = {
    animalId: string;
    chipId: string;
    chipReaded: boolean;
    id: string;
    ownerId: string;
    pdfUrl: string | null;
    date: Date | string;
    ownerSignature: string;
    veterinarianSignature: string;
    veterinaryId: string;
}
